import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["folderId", "folderPath", "fileName"];
/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable require-await */
/* eslint-disable hubspot-dev/no-async-await */

import { List, Map as ImmutableMap, fromJS } from 'immutable';
import { completeMultiPartUpload as completeMultiPartUploadRequest } from 'file-manager-components/api/uploadFile';
import http from 'hub-http/clients/apiClient';
import getApiUrl from '../utils/getApiUrl';
import { ObjectCategory } from '../Constants';
import { FileAccessValues } from '../enums/InternalFileManagerFileAccess';
import { fromPublicFMFile } from '../types/FMFile';
import { localUrlToFileUrl } from 'file-manager-components/internal/utils';
import { trimFileName } from 'file-manager-components/utils/trimFileName';
import { fetchSignedUrl, getSignedRedirectUrl } from 'file-manager-components/api/signedUrl';
const BASE_URI = 'filemanager/api/v2/files';
const BASE_V3_URI = 'filemanager/api/v3/files';
export const buildFileFromAttrs = attrs => {
  attrs.category = ObjectCategory.FILE;
  return fromJS(attrs);
};
const buildFilesPayload = resp => ImmutableMap(Object.assign({}, resp, {
  objects: List(resp.objects.map(buildFileFromAttrs))
}));
export function archiveFile(fileId) {
  const URI = `${BASE_URI}/${fileId}/archive`;
  return http.post(URI, {
    data: {
      archived: true
    }
  }).then(buildFileFromAttrs);
}
export function unarchiveFile(fileId) {
  const URI = `${BASE_URI}/${fileId}/archive`;
  return http.post(URI, {
    data: {
      archived: false
    }
  }).then(buildFileFromAttrs);
}
export async function fetchSingleFile(fileId) {
  return http.get(`${BASE_V3_URI}/${fileId}`).then(buildFileFromAttrs);
}
export async function move(fileId, options) {
  const URI = `${BASE_V3_URI}/${fileId}/move-file`;
  return http.post(URI, {
    data: Object.assign({}, options.name && {
      newName: options.name
    }, options.folder_id !== undefined && {
      newParentFolderId: options.folder_id
    })
  }).then(buildFileFromAttrs);
}
const sanitizeFileName = fileName => fileName.replace(/\./gm, '-');
const COMMON_FILE_UPLOAD_PARAMS = {
  timeout: 0,
  headers: {
    'content-type': false
  }
};
export const uploadFileV3 = (file, uploadedFileAccess, fileUploadOptions = {}, {
  updateProgress,
  xhrCallback,
  httpClient = http
} = {}) => {
  if (!file) {
    throw new Error('`file` arg is required and must be a local File');
  }
  if (!Object.keys(FileAccessValues).includes(uploadedFileAccess)) {
    throw new Error('[FileManagerCore/uploadFile(V3)] Missing or invalid uploadedFileAccess param. Visit https://product.hubteam.com/docs/file-manager-manual/Frontend/index.html for details.');
  }
  const {
      folderId,
      folderPath,
      fileName
    } = fileUploadOptions,
    extraOptions = _objectWithoutPropertiesLoose(fileUploadOptions, _excluded);
  extraOptions.access = uploadedFileAccess;
  let preNormalizedFileName;
  if (fileName) {
    preNormalizedFileName = fileName;
  } else if (file instanceof File && file.name) {
    preNormalizedFileName = file.name;
  }
  const formData = new FormData();
  if (preNormalizedFileName && !fileName) {
    const normalizedFileName = trimFileName(preNormalizedFileName).normalize('NFKC');
    formData.append('file', file, normalizedFileName);
  } else {
    formData.append('file', file);
  }
  if (folderId) {
    formData.append('folderId', folderId.toString());
  }
  if (folderPath) {
    formData.append('folderPath', folderPath);
  }
  if (fileName) {
    const trimmedFileName = trimFileName(fileName);
    formData.append('fileName', fileUploadOptions.preserveExtension ? trimmedFileName : sanitizeFileName(trimmedFileName));
  }
  formData.append('options', JSON.stringify(extraOptions));
  return httpClient.post(`${BASE_V3_URI}/upload`, Object.assign({}, COMMON_FILE_UPLOAD_PARAMS, {
    data: formData,
    withXhr: xhr => {
      xhr.upload.addEventListener('progress', updateProgress, false);
      if (xhrCallback) {
        xhrCallback(xhr);
      }
    }
  })).then(buildFilesPayload);
};
export const uploadFile = uploadFileV3;
export const completeMultiPartUpload = (fileSize, fileName, uploadId, filePartETags, multipartUploadKeyName, fileUploadOptions) => completeMultiPartUploadRequest(fileSize, fileName, uploadId, filePartETags, multipartUploadKeyName, fileUploadOptions).then(({
  objects
}) => buildFileFromAttrs(fromPublicFMFile(objects[0])));
export const completeMultiPartReplace = (fileId, uploadedFileAccess, multipartUploadKeyName, uploadId, filePartETags) => http.post(`${BASE_V3_URI}/multipart-upload/replace`, {
  data: {
    fileId,
    options: {
      access: uploadedFileAccess
    },
    key: multipartUploadKeyName,
    uploadId,
    filePartETags
  }
}).then(buildFileFromAttrs);
export const replaceFile = async (fileId, file, uploadedFileAccess, {
  updateProgress
} = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('options', JSON.stringify({
    access: uploadedFileAccess
  }));
  return http.post(`${BASE_V3_URI}/${fileId}/replace`, Object.assign({}, COMMON_FILE_UPLOAD_PARAMS, {
    data: formData,
    withXhr: xhr => {
      xhr.upload.addEventListener('progress', updateProgress, false);
    }
  })).then(buildFilesPayload);
};
export async function uploadResizedImage(fileId, newWidth) {
  return http.post(`${BASE_URI}/${fileId}/resize-image`, {
    timeout: 0,
    query: {
      width: newWidth
    }
  }).then(buildFileFromAttrs);
}
export async function recalculateImageDimensions(fileId) {
  return http.post(`${BASE_V3_URI}/${fileId}/recalculate-image-dimensions`, {
    timeout: 5000
  }).then(buildFileFromAttrs);
}
export async function exportFiles(emailAddress, filesAndFolders = {}) {
  const uri = `${BASE_URI}/export-all-files`;
  return http.post(uri, {
    data: Object.assign({
      email_address: emailAddress
    }, filesAndFolders)
  }).then(fromJS);
}
const LOCAL_FILE_PATHS = ['local.hsappstatic.net/FileManagerImages'];
export async function downloadFromUrl({
  fileDownloadOptions = {},
  url,
  uploadedFileAccess
}) {
  if (!Object.keys(FileAccessValues).includes(uploadedFileAccess)) {
    throw new Error('[FileManagerCore/downloadFromUrl] Missing or invalid uploadedFileAccess param. Visit https://product.hubteam.com/docs/file-manager-manual/Frontend/index.html for details.');
  }

  /**
   * If we're in development mode, we may be pointing to a locally served file, e.g. from FileManagerImages
   * To prevent a 400 error from the backend trying to reference a local file, we upload the file to the backend instead of downloading from a URL
   * This code will get stripped out by Webpack when we build for QA/Production
   */
  if (process.env.NODE_ENV === 'development') {
    if (LOCAL_FILE_PATHS.some(path => url.includes(path))) {
      const file = await localUrlToFileUrl(url);
      const fileUploadResponse = await uploadFileV3(file, uploadedFileAccess, fileDownloadOptions);
      return fileUploadResponse.getIn(['objects', 0]);
    }
  }
  return http.post(`${BASE_V3_URI}/synchronous-download-from-url`, {
    timeout: 60000,
    data: Object.assign({}, fileDownloadOptions, {
      access: uploadedFileAccess,
      url
    })
  }).then(buildFileFromAttrs);
}

// Note that this will copy with no TTL by default unless specified
export async function copyFile({
  fileId,
  requestBody
}) {
  return http.post(`${BASE_URI}/${fileId}/copy-file`, {
    data: requestBody
  }).then(buildFileFromAttrs);
}
export function remove(fileId) {
  return http.delete(`${BASE_URI}/${fileId}`);
}
export function getDownloadUrl(path, version) {
  return getApiUrl(`${BASE_URI}/download/${path}`, {
    version
  });
}
export async function removeCanvaId(fileId) {
  return http.delete(`${BASE_V3_URI}/${fileId}/canva`).then(buildFileFromAttrs);
}

// Deprecated: use fetchSignedUrl from file-manager-components/api/signedUrl instead
export function getSignedUrl(fileId, options = {}) {
  return fetchSignedUrl(fileId, options);
}

// Deprecated: use fetchSignedUrl from file-manager-components/api/signedUrl with isAttachment: true
export function getDownloadableSignedUrl(fileId, options = {}) {
  return fetchSignedUrl(fileId, Object.assign({}, options, {
    isAttachment: true
  }));
}

// Deprecated: use getSignedRedirectUrl from file-manager-components/api/signedUrl with isAttachment: true
export function getSignedUrlRedirectDownloadUrl(fileId, options = {}) {
  return getSignedRedirectUrl(fileId, Object.assign({}, options, {
    isAttachment: true
  }));
}